import { useEffect } from 'react';

import { cx } from '@emotion/css';
import { Col, Form, Row } from 'antd';
import { Formik } from 'formik';
import { isNumber } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { doUpdateOrder } from 'src/actions';
import { useAccountBalance } from 'src/hooks';
import { Order, Quote } from 'src/models';
import { SecurityDetailsCarousel } from 'src/pages/Summary/SecurityDetailsCarousel';
import { AlignItems, ColorConstant, Display, JustifyContent, Spacing } from 'src/styles';
import { Maybe } from 'src/utils';
import * as Yup from 'yup';

import { CurrencyField } from '../CurrencyField/CurrencyField';
import { MButton } from '../MButton/MButton';
import { MFormCurrencyInput } from '../MFormCurrencyInput/MFormCurrencyInput';
import { MFormTextField } from '../MFormTextField/MFormTextField';
import { MModalBody, MModalFooter, MModalTitle } from '../MModal/SinglePageModal';
import { MTypography } from '../MTypography/MTypography';

export interface ChangeOrderFormValues {
  price?: number;
  stop?: number;
}

const isSamePrice = (value: unknown, current?: number): boolean => {
  const next = Number(value);
  const previous = Number(current);

  if (Number.isNaN(next) || Number.isNaN(previous)) {
    return false;
  }

  return next.toFixed(2) === previous.toFixed(2);
};

interface ChangeOrderStepProps {
  order: Order;
  accountId: number;
  initialValues: ChangeOrderFormValues;
  onClose: () => void;
  onSuccess: (values: ChangeOrderFormValues) => void;
  onError: (values: ChangeOrderFormValues) => void;
}

export const ChangeOrderStep = ({
  order,
  accountId,
  initialValues,
  onClose,
  onSuccess,
  onError,
}: ChangeOrderStepProps) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state: any) => Boolean(state.orders.update.isLoading));
  const isSuccess = useSelector((state: any) => Boolean(state.orders.update.__succeeded));
  const isError = useSelector((state: any) => Boolean(state.orders.update.__failed));
  const quoteDetails: Maybe<Quote> = useSelector((state: any) => state.quotes.details.data);
  const { balance } = useAccountBalance();
  const hasLimitPrice = order.type.isLimit || order.type.isStopLimit;
  const hasStopPrice = order.type.isStopMarket || order.type.isStopLimit;
  const lastPrice = quoteDetails?.symbol === order.symbol ? quoteDetails?.last : undefined;

  const validationSchema = Yup.object({
    price: hasLimitPrice
      ? Yup.number()
          .typeError('Limit Price must be a number')
          .min(0.01, 'Limit Price must be at least 0.01')
          .required('Limit Price is required')
      : Yup.number().notRequired(),
    stop: hasStopPrice
      ? Yup.number()
          .typeError('Stop Price must be a number')
          .min(0.01, 'Stop Price must be at least 0.01')
          .required('Stop Price is required')
      : Yup.number().notRequired(),
  }).test('changed-price', 'Limit or Stop Price must be different from current Limit or Stop Price', function (values) {
    const limitChanged = hasLimitPrice && !isSamePrice(values.price, order.limit);
    const stopChanged = hasStopPrice && !isSamePrice(values.stop, order.stop);

    if (hasLimitPrice && hasStopPrice) {
      if (limitChanged || stopChanged) {
        return true;
      }

      const message = 'Limit or Stop Price must be different from current Limit or Stop Price';

      return new Yup.ValidationError(
        [this.createError({ path: 'price', message }), this.createError({ path: 'stop', message })],
        values,
        'changed-price',
      );
    }

    if (hasLimitPrice && !limitChanged) {
      return this.createError({
        path: 'price',
        message: 'Limit Price must be different from the current Limit Price',
      });
    }

    if (hasStopPrice && !stopChanged) {
      return this.createError({
        path: 'stop',
        message: 'Stop Price must be different from the current Stop Price',
      });
    }

    return true;
  });

  return (
    <Formik<ChangeOrderFormValues>
      initialValues={initialValues}
      initialErrors={{}}
      validationSchema={validationSchema}
      validateOnChange
      enableReinitialize
      onSubmit={values => {
        dispatch(
          doUpdateOrder({
            params: { id: order.id, accountId },
            body: {
              orderType: order.type.value,
              price: hasLimitPrice ? Number(values.price) : undefined,
              stop: hasStopPrice ? Number(values.stop) : undefined,
            },
          }),
        );
      }}>
      {form => {
        useEffect(() => {
          if (isSuccess) {
            onSuccess({
              price: hasLimitPrice ? Number(form.values.price) : undefined,
              stop: hasStopPrice ? Number(form.values.stop) : undefined,
            });
          }
        }, [isSuccess]);

        useEffect(() => {
          if (isError) {
            onError({
              price: hasLimitPrice ? Number(form.values.price) : undefined,
              stop: hasStopPrice ? Number(form.values.stop) : undefined,
            });
          }
        }, [isError]);

        return (
          <Form autoComplete='off' style={{ width: '100%' }} onFinish={form.submitForm}>
            <MModalTitle>Change {order.action.label} Security Order</MModalTitle>
            <MModalBody>
              <Col span={24} className={Spacing.my12} data-testid={'change-order-name-value'}>
                <MTypography variant='h2' align='center' color={ColorConstant.BRAND6} weight='semibold'>
                  {order.action.label} {order.symbol}
                </MTypography>
              </Col>
              <Col span={24} className={Spacing.my12}>
                <MTypography variant='h6' align='center' weight='light' testId='change-order-cash-available'>
                  Cash Available: <CurrencyField value={balance.cashAvailable} />
                </MTypography>
              </Col>
              <Col span={24} className={Spacing.my24}>
                <Row>
                  <Col span={24}>
                    <MTypography variant='h2' align='center' color={ColorConstant.BRAND6} weight='semibold'>
                      {isNumber(lastPrice) ? (
                        <CurrencyField value={lastPrice} testId={'change-order-last-price-value'} />
                      ) : (
                        '-'
                      )}
                    </MTypography>
                  </Col>
                  <Col span={24}>
                    <MTypography variant='h6' align='center' weight='light'>
                      Last Price
                    </MTypography>
                  </Col>
                </Row>
              </Col>
              <Col span={24} className={cx(Spacing.mt24, Spacing.mb32)}>
                <SecurityDetailsCarousel symbol={order.symbol} />
              </Col>
              <Col span={24}>
                <MFormTextField label='Order Type' value={order.type.label} testId='change-order-order-type' />
              </Col>
              <Col span={24}>
                <MFormTextField label='Share Quantity' value={order.quantity} testId='change-order-quantity' />
              </Col>
              {hasLimitPrice && (
                <Col span={24}>
                  <MFormTextField
                    label='Current Limit Price'
                    value={<CurrencyField value={order.limit} />}
                    testId='change-order-current-price'
                  />
                </Col>
              )}
              {hasStopPrice && (
                <Col span={24}>
                  <MFormTextField
                    label='Current Stop Price'
                    value={<CurrencyField value={order.stop} />}
                    testId='change-order-current-stop'
                  />
                </Col>
              )}
              {hasLimitPrice && (
                <Col span={24}>
                  <MFormCurrencyInput
                    label='Limit Price'
                    value={form.values.price}
                    onChange={value => {
                      form.setFieldValue('price', value);
                    }}
                    error={form.errors.price}
                    name='price'
                    autoComplete='off'
                    className={cx(Spacing.my0, Spacing.py16)}
                    testId='change-order-price'
                  />
                </Col>
              )}
              {hasStopPrice && (
                <Col span={24}>
                  <MFormCurrencyInput
                    label='Stop Price'
                    value={form.values.stop}
                    onChange={value => {
                      form.setFieldValue('stop', value);
                    }}
                    error={form.errors.stop}
                    name='stop'
                    autoComplete='off'
                    className={cx(Spacing.my0, Spacing.py16)}
                    testId='change-order-stop'
                  />
                </Col>
              )}
            </MModalBody>
            <MModalFooter className={cx(Display.flex, AlignItems.center, JustifyContent.spaceBetween)}>
              <MButton type='tertiary' onClick={onClose} disabled={isLoading}>
                Close
              </MButton>
              <MButton htmlType='submit' loading={isLoading} testId='change-order-save'>
                Save
              </MButton>
            </MModalFooter>
          </Form>
        );
      }}
    </Formik>
  );
};
